import { Link, useNavigate } from 'react-router-dom';
// Hvorfor oss-bilde: Supabase Storage (anbefalt) eller fallback til static
const partnerImage = '/static/partner-growth-v3.png';
import { ArrowRight, Globe, Search, Megaphone, ShoppingCart, Palette, MapPin, ChevronDown, Check, BarChart3 } from 'lucide-react';
import { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { PageSEO } from '@/components/SEO';
import axios from 'axios';

const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;

function getApiBase() {
  const url = process.env.REACT_APP_BACKEND_URL;
  if (url && url !== 'undefined') return `${url.replace(/\/$/, '')}/api`;
  if (typeof window !== 'undefined') return `${window.location.origin}/api`;
  return '/api';
}

const tjenester = [
  {
    icon: Globe,
    title: 'Ny nettside',
    description: 'Profesjonelle og mobilvennlige nettsider som gir resultater.',
    href: '/tjenester/nettside',
  },
  {
    icon: Search,
    title: 'SEO',
    description: 'Bli synlig på Google og tiltrekk flere betalende kunder.',
    href: '/tjenester/seo',
  },
  {
    icon: Megaphone,
    title: 'Annonsering',
    description: 'Målrettet digital annonsering som gir avkastning.',
    href: '/tjenester/annonsering',
  },
  {
    icon: ShoppingCart,
    title: 'Nettbutikk',
    description: 'Raske, mobilvennlige nettbutikker som selger.',
    href: '/tjenester/nettbutikk',
  },
  {
    icon: Palette,
    title: 'Logodesign',
    description: 'Profesjonelle logoer som bygger tillit.',
    href: '/tjenester/logodesign',
  },
  {
    icon: MapPin,
    title: 'Google Bedriftsprofil',
    description: 'Bli funnet når kundene søker lokalt.',
    href: '/tjenester/google-bedriftsprofil',
  },
];

const prosess = [
  {
    num: '01',
    title: 'Kartlegging av behov',
    description: 'Vi starter med å forstå virksomheten din, målgruppen din og hva du ønsker å oppnå.',
  },
  {
    num: '02',
    title: 'Plan og løsning',
    description: 'Basert på kartleggingen utvikler vi en tydelig plan med fokus på brukervennlighet og synlighet.',
  },
  {
    num: '03',
    title: 'Gjennomføring',
    description: 'Vi utvikler og lanserer løsningen, og følger opp med justeringer for beste resultater.',
  },
];

const faqs = [
  {
    question: 'Hvordan skiller vi oss fra andre byråer?',
    answer: 'Vi selger ikke tjenester enkeltvis, men setter sammen en strategi som passer dine mål og budsjetter. Dette gir deg en helhetlig markedsføringsplan som gir best mulig resultater.',
  },
  {
    question: 'Hva kan jeg forvente av resultater?',
    answer: 'Resultatene avhenger av flere faktorer, som merkevare, konkurranse, budsjett og evnen til å konvertere leads til kunder. Vi jobber langsiktig, og alle våre kunder har oppnådd lønnsomhet innen 12 måneder.',
  },
  {
    question: 'Hvor mye koster det?',
    answer: 'Prisen avhenger av målene dine og hva vi skal gjøre for å nå dem. Etter en grundig gjennomgang skreddersyr vi en strategi basert på ditt budsjett.',
  },
  {
    question: 'Hvor lang tid tar det å se resultater?',
    answer: 'For SEO og organisk trafikk kan det ta 3-6 måneder før du ser betydelige resultater. For annonsering ser du ofte resultater umiddelbart, men optimalisering tar tid.',
  },
];

export default function Home() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', phone: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);

  const validatePhone = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, '');
    return digits.length === 8;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError(null);
    setPhoneError(null);

    if (formData.phone && !validatePhone(formData.phone)) {
      setPhoneError('Telefonnummer må ha nøyaktig 8 siffer.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Get reCAPTCHA token
      let recaptcha_token = null;
      if (window.grecaptcha) {
        try {
          recaptcha_token = await new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
              window.grecaptcha.execute('6LdMuo0sAAAAAJGFT2Wi257JosNQSehbrGsYtsXo', { action: 'home_contact_submit' })
                .then(resolve)
                .catch(reject);
            });
          });
        } catch (recaptchaErr) {
          console.error('reCAPTCHA error:', recaptchaErr);
        }
      }

      if (FORMSPREE_ID) {
        const response = await axios.post(
          `https://formspree.io/f/${FORMSPREE_ID}`,
          {
            name: formData.name,
            email: 'ikke-oppgitt@sidekraft.no',
            phone: formData.phone,
            message: formData.message,
            _subject: `Gratis konsultasjon fra ${formData.name}`,
            'g-recaptcha-response': recaptcha_token
          },
          { headers: { 'Content-Type': 'application/json', Accept: 'application/json' } }
        );
        if (response.data?.ok) {
          navigate('/takk');
        } else {
          setFormError(response.data?.error || 'Noe gikk galt. Vennligst prøv igjen.');
        }
      } else {
        const apiBase = getApiBase();
        const response = await axios.post(`${apiBase}/contact`, {
          name: formData.name,
          email: 'ikke-oppgitt@sidekraft.no',
          phone: formData.phone,
          message: formData.message,
          recaptcha_token
        });
        if (response.data?.status === 'success') {
          navigate('/takk');
        } else {
          setFormError(response.data?.message || 'Noe gikk galt. Vennligst prøv igjen.');
        }
      }
    } catch (err) {
      console.error('Error:', err);
      setFormError('Kunne ikke sende meldingen. Vennligst prøv igjen eller ring oss på 400 81 956.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-hidden">
      <PageSEO
        title="Side Kraft | Digitalt markedsføringsbyrå Norge – Synlig på nett"
        path="/"
        description="Side Kraft er ditt digitale markedsføringsbyrå i Norge. Vi leverer profesjonelle nettsider, SEO og Google Ads som gir vekst. Bli synlig på Google – kontakt oss i dag!"
      />
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-start" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/80 z-10" />
          <img
            src="https://images.unsplash.com/photo-1554465861-79f44edf8020?auto=format&fit=crop&w=1280&q=75"
            srcSet="https://images.unsplash.com/photo-1554465861-79f44edf8020?auto=format&fit=crop&w=640&q=75 640w, https://images.unsplash.com/photo-1554465861-79f44edf8020?auto=format&fit=crop&w=1280&q=75 1280w, https://images.unsplash.com/photo-1554465861-79f44edf8020?auto=format&fit=crop&w=1920&q=75 1920w"
            sizes="100vw"
            alt="Moderne kontor – Side Kraft digitalt markedsføringsbyrå Oslo"
            fetchPriority="high"
            decoding="async"
            className="w-full h-full object-cover opacity-40"
          />
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-6 lg:px-12 pt-16 md:pt-20">
          <div className="max-w-3xl">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-6 animate-fade-in">
              Digitalt markedsføringsbyrå Oslo
            </p>
            <h1 className="font-syne font-bold text-5xl md:text-6xl lg:text-7xl text-primary leading-[0.95] tracking-tight animate-fade-in-up">
              Vi gjør bedriften din synlig på nett
            </h1>
            <p className="font-manrope text-lg md:text-xl text-muted-foreground mt-8 max-w-xl leading-relaxed" style={{ animationDelay: '0.2s' }}>
              Markedsføring handler om å være til stede der folk er. Side Kraft hjelper norske bedrifter med profesjonelle nettsider, SEO og Google Ads – slik at kundene finner deg.
            </p>
            <div className="flex flex-wrap gap-4 mt-10" style={{ animationDelay: '0.4s' }}>
              <Link
                to="/kontakt"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
                data-testid="hero-cta-primary"
              >
                Kontakt oss
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <a
                href="#tjenester"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-300"
                data-testid="hero-cta-secondary"
              >
                Se tjenester
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-6 h-6 text-muted-foreground" />
        </div>
      </section>

      {/* Services Section */}
      <section id="tjenester" className="py-24 md:py-32 bg-muted" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Våre tjenester
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight">
              Markedsføring gjort riktig
            </h2>
            <p className="font-manrope text-muted-foreground mt-4 leading-relaxed">
              Markedsføring handler om å være til stede der folk er, og at det skal være enkelt å finne informasjon om din bedrift. Vi er et digitalt byrå som er eksperter på nettsider, SEO og Google Ads.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tjenester.map((tjeneste, index) => (
              <Link
                key={tjeneste.href}
                to={tjeneste.href}
                className="group bg-card border border-border/50 p-8 rounded-2xl shadow-sm hover:shadow-xl hover:border-accent/50 transition-all duration-500 relative overflow-hidden active:scale-[0.98]"
                data-testid={`service-card-${index}`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 transform translate-x-16 -translate-y-16 group-hover:translate-x-12 group-hover:-translate-y-12 transition-transform duration-500" />

                <tjeneste.icon className="w-10 h-10 text-accent mb-6" />

                <h3 className="font-syne font-bold text-xl text-primary mb-3">
                  {tjeneste.title}
                </h3>
                <p className="font-manrope text-sm text-muted-foreground leading-relaxed mb-6">
                  {tjeneste.description}
                </p>

                <span className="inline-flex items-center gap-2 font-manrope text-sm font-medium text-primary group-hover:text-accent transition-colors duration-300">
                  Les mer
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why Us Section */}
      <section className="py-24 md:py-32" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                Hvorfor oss?
              </p>
              <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-8">
                Din partner for digital vekst
              </h2>
              <p className="font-manrope text-muted-foreground leading-relaxed mb-8">
                Side Kraft er et digitalt markedsføringsbyrå som tar hånd om din digitale markedsføring, så du kan fokusere på det du kan best. Noe av det viktigste for bedriften din er at potensielle kunder vet du finnes. Vi hjelper deg med profesjonelle nettsider, søkemotoroptimalisering (SEO) og målrettet annonsering – slik at kundene finner produktet eller tjenesten du tilbyr.
              </p>

              <ul className="space-y-4 mb-10">
                {[
                  'Skreddersydd strategi for din bedrift',
                  'Transparent rapportering og kommunikasjon',
                  'Fokus på målbare resultater',
                  'Erfarne spesialister i teamet',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                    <span className="font-manrope text-sm text-secondary-foreground">{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/om-oss"
                className="inline-flex items-center gap-2 font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 group"
                data-testid="why-us-cta"
              >
                Les mer om oss
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
            </div>

            <div className="relative min-h-[500px] bg-muted rounded-3xl overflow-hidden shadow-2xl ring-1 ring-primary/5 group">
              <div className="absolute -top-4 -left-4 w-full h-full border-2 border-accent/20 z-0 rounded-3xl" />
              <img
                src={partnerImage}
                alt="Digital vekst i hele Norge – Side Kraft profesjonelt team"
                loading="eager"
                decoding="async"
                className="relative z-10 w-full h-[500px] object-cover bg-muted min-h-[300px] !opacity-100 transition-all duration-700 group-hover:scale-[1.02]"
                style={{ opacity: 1 }}
                onError={(e) => {
                  e.target.onerror = null;
                  // If even the static asset fails, use a reliable placeholder
                  e.target.src = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=75';
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - SEO trust signals (inspirert av verdimedia.no) */}
      <section className="py-24 md:py-32 bg-muted" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <div>
              <p className="font-syne font-bold text-4xl md:text-5xl text-accent mb-2">100+</p>
              <h3 className="font-syne font-bold text-lg text-primary mb-2">KUNDER</h3>
              <p className="font-manrope text-sm text-muted-foreground">
                Vi hjelper norske bedrifter med bedre synlighet på nett gjennom profesjonelle nettsider og SEO.
              </p>
            </div>
            <div>
              <p className="font-syne font-bold text-4xl md:text-5xl text-accent mb-2">150+</p>
              <h3 className="font-syne font-bold text-lg text-primary mb-2">PROSJEKTER</h3>
              <p className="font-manrope text-sm text-muted-foreground">
                Nettsider, Google Ads og digital markedsføring – resultatet er bedre synlighet og økt omsetning.
              </p>
            </div>
            <div>
              <p className="font-syne font-bold text-4xl md:text-5xl text-accent mb-2">5+</p>
              <h3 className="font-syne font-bold text-lg text-primary mb-2">ÅR ERFARING</h3>
              <p className="font-manrope text-sm text-muted-foreground">
                Erfarne spesialister innen SEO, nettsider og digital annonsering for norske bedrifter.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 md:py-32 bg-primary text-primary-foreground" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-2xl mb-16">
            <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
              Vår prosess
            </p>
            <h2 className="font-syne font-bold text-4xl md:text-5xl tracking-tight">
              Slik hjelper vi deg
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {prosess.map((step, index) => (
              <div
                key={index}
                className="relative p-8 border border-primary-foreground/10 rounded-2xl hover:border-accent/50 transition-all duration-500 hover:shadow-lg group"
                data-testid={`process-step-${index}`}
              >
                <span className="font-syne font-bold text-6xl text-accent/20 absolute top-4 right-4">
                  {step.num}
                </span>
                <h3 className="font-syne font-bold text-xl mb-4 relative z-10">
                  {step.title}
                </h3>
                <p className="font-manrope text-sm text-primary-foreground/70 leading-relaxed relative z-10">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 md:py-32" data-testid="faq-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <p className="font-manrope text-xs font-medium tracking-[0.3em] uppercase text-accent mb-4">
                FAQ
              </p>
              <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
                Alle spørsmålene du har fortjener et svar
              </h2>
              <p className="font-manrope text-muted-foreground leading-relaxed">
                Har du spørsmål vi ikke har svart på? Ta gjerne kontakt med oss direkte.
              </p>
            </div>

            <div>
              <Accordion type="single" collapsible className="space-y-4">
                {faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`item-${index}`}
                    className="border border-border px-6 data-[state=open]:border-accent/50"
                    data-testid={`faq-item-${index}`}
                  >
                    <AccordionTrigger className="font-syne font-bold text-left py-6 hover:no-underline hover:text-accent transition-colors duration-300">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="font-manrope text-muted-foreground pb-6 leading-relaxed">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-muted" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="font-syne font-bold text-4xl md:text-5xl text-primary tracking-tight mb-6">
                Klar for å ta neste steg digitalt?
              </h2>
              <p className="font-manrope text-muted-foreground leading-relaxed mb-8">
                Vi har solid erfaring og god bransjekunnskap, og hjelper ambisiøse bedrifter med å oppnå gode resultater gjennom profesjonelle digitale løsninger. Ta kontakt for en gratis konsultasjon.
              </p>
              <div className="flex items-center gap-4">
                <a
                  href="tel:+4740081956"
                  onClick={(e) => {
                    e.preventDefault();
                    if (typeof window.gtag_report_conversion === 'function') {
                      window.gtag_report_conversion('tel:+4740081956');
                    } else {
                      window.location.href = 'tel:+4740081956';
                    }
                  }}
                  className="font-syne font-bold text-2xl text-accent hover:text-primary transition-colors duration-300"
                  data-testid="cta-phone"
                >
                  +47 400 81 956
                </a>
              </div>
            </div>

            <div>
              <form onSubmit={handleSubmit} className="bg-card p-8 border border-border" data-testid="contact-form">
                <h3 className="font-syne font-bold text-xl text-primary mb-6">
                  Få en gratis konsultasjon
                </h3>

                <div className="space-y-6">
                  {formError && (
                    <div className="p-4 bg-destructive/10 border border-destructive rounded">
                      <p className="font-manrope text-sm text-destructive">{formError}</p>
                    </div>
                  )}
                  <div>
                    <input
                      type="text"
                      placeholder="Fullt navn"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full bg-transparent border-b-2 border-border focus:border-primary px-0 py-4 focus:outline-none focus:ring-0 placeholder:text-muted-foreground/50 font-manrope text-lg transition-colors duration-300"
                      required
                      data-testid="form-name"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      placeholder="Telefon (8 siffer)"
                      value={formData.phone}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 8);
                        setFormData({ ...formData, phone: val });
                        setPhoneError(null);
                      }}
                      maxLength={8}
                      className={`w-full bg-transparent border-b-2 px-0 py-4 focus:outline-none focus:ring-0 placeholder:text-muted-foreground/50 font-manrope text-lg transition-colors duration-300 ${phoneError ? 'border-destructive focus:border-destructive' : 'border-border focus:border-primary'}`}
                      required
                      data-testid="form-phone"
                    />
                    {phoneError && (
                      <p className="font-manrope text-sm text-destructive mt-2">{phoneError}</p>
                    )}
                  </div>
                  <div>
                    <textarea
                      placeholder="Melding"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={4}
                      className="w-full bg-transparent border-b-2 border-border focus:border-primary px-0 py-4 focus:outline-none focus:ring-0 placeholder:text-muted-foreground/50 font-manrope text-lg resize-none transition-colors duration-300"
                      required
                      data-testid="form-message"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full font-syne font-bold text-sm uppercase tracking-wide px-8 py-4 bg-primary text-primary-foreground hover:bg-accent transition-colors duration-300 disabled:opacity-50"
                    data-testid="form-submit"
                  >
                    {isSubmitting ? 'Sender...' : 'Send henvendelse'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
